import React from 'react';

declare global {
    interface Window {
        analytics?: {
            track(event: string, properties?: Record<string, any>): void;
            identify(userId: string, traits?: Record<string, any>): void;
            page(name?: string, properties?: Record<string, any>): void;
            load(writeKey: string, options?: any): void;
            ready(callback: () => void): void;
            reset(): void;
            group(groupId: string, traits?: Record<string, any>): void;
            alias(userId: string, previousId?: string): void;
            screen(name?: string, properties?: Record<string, any>): void;
            debug(enabled?: boolean): void;
            initialized?: boolean;
            invoked?: boolean;
            methods?: string[];
            factory?: (method: string) => (...args: any[]) => any;
            push?: (args: any[]) => void;
            _writeKey?: string;
            _loadOptions?: any;
            SNIPPET_VERSION?: string;
        };
    }
}

const loadSegment = () => {
    const i = 'analytics';
    const analytics = ((window as any)[i] = (window as any)[i] || []);

    if (!analytics.initialize) {
        if (analytics.invoked) {
            window.console && console.error && console.error('Segment snippet included twice.');
        } else {
            analytics.invoked = true;
            analytics.methods = [
                'trackSubmit',
                'trackClick',
                'trackLink',
                'trackForm',
                'pageview',
                'identify',
                'reset',
                'group',
                'track',
                'ready',
                'alias',
                'debug',
                'page',
                'screen',
                'once',
                'off',
                'on',
                'addSourceMiddleware',
                'addIntegrationMiddleware',
                'setAnonymousId',
                'addDestinationMiddleware',
                'register'
            ];

            analytics.factory = function (method: string) {
                return function (...args: any[]) {
                    if ((window as any)[i].initialized) {
                        return (window as any)[i][method](...args);
                    }

                    const n = Array.prototype.slice.call(args);
                    if (
                        ['track', 'screen', 'alias', 'group', 'page', 'identify'].indexOf(method) >
                        -1
                    ) {
                        const c = document.querySelector("link[rel='canonical']");
                        n.push({
                            __t: 'bpc',
                            c: (c && c.getAttribute('href')) || undefined,
                            p: location.pathname,
                            u: location.href,
                            s: location.search,
                            t: document.title,
                            r: document.referrer
                        });
                    }
                    n.unshift(method);
                    analytics.push(n);
                    return analytics;
                };
            };

            for (let n = 0; n < analytics.methods.length; n++) {
                const key = analytics.methods[n];
                analytics[key] = analytics.factory(key);
            }

            analytics.load = function (key: string, options?: any) {
                const t = document.createElement('script');
                t.type = 'text/javascript';
                t.async = true;
                t.setAttribute('data-global-segment-analytics-key', i);
                t.src = 'https://cdn.segment.com/analytics.js/v1/' + key + '/analytics.min.js';
                const r = document.getElementsByTagName('script')[0];
                if (r.parentNode) {
                    r.parentNode.insertBefore(t, r);
                }
                analytics._loadOptions = options;
            };

            analytics._writeKey = process.env.REACT_APP_SEGMENT_KEY || '';
            analytics.SNIPPET_VERSION = '5.2.1';

            analytics.load(process.env.REACT_APP_SEGMENT_KEY);
        }
    }
};

const SegmentInit: React.FC = () => {
    React.useEffect(loadSegment, []);

    return null;
};

export default SegmentInit;

// Helper functions for common tracking events
export const segmentTrack = (event: string, properties?: Record<string, any>) => {
    if (window.analytics) {
        window.analytics.track(event, properties);
    }
};

export const segmentIdentify = (userId: string, traits?: Record<string, any>) => {
    if (window.analytics) {
        window.analytics.identify(userId, traits);
    }
};

export const segmentPage = (name?: string, properties?: Record<string, any>) => {
    if (window.analytics) {
        window.analytics.page(name, properties);
    }
};
